import de from './de.json'
import en from './en.json'
import { activeLocale, formats, type Locale } from './locale'

/**
 * The strings, and the one function that reads them.
 *
 * No i18n library. The locale is fixed at launch and never changes, there is one namespace and
 * two bundles, so a detector, lazy loading and a suspense boundary would all be machinery with
 * nothing to do — and the library's own failure mode for a missing key is a console warning at
 * runtime, which is the wrong one for this repository. Here `de.json` is typed against
 * `en.json`, so a forgotten translation is a `tsc` error and CI typechecks before it tests.
 */

export type Key = keyof typeof en

/** Missing key in de.json → this line fails to compile, naming the key. */
const bundles: Record<Locale, Record<Key, string>> = { en, de }

/** Orphan key in de.json → this fails to compile with the offending key in the message. A key
 * en.json does not have is a typo, and a typo'd translation is one nobody will ever read. */
type Assert<T extends true> = T
type NoOrphans<Extra extends string> = [Extra] extends [never] ? true : { 'key not in en.json': Extra }
export type NoOrphanGermanKeys = Assert<NoOrphans<Exclude<keyof typeof de, Key>>>

/**
 * `{name}` placeholders are filled from `params`. They are not type-checked against the string:
 * `resolveJsonModule` widens every JSON value to `string`, so the literal a template could be
 * read from is gone by the time the type system sees it.
 */
export function t(key: Key, params?: Params): string {
  return fill(bundles[activeLocale().locale][key], params)
}

/** Shared with the rule-message bundle, whose keys are rule ids and cannot be typed here. */
export function fill(text: string, params?: Params): string {
  if (!params) return text
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name]
    return value === undefined ? whole : String(value)
  })
}

export type Params = Readonly<Record<string, string | number>>

/**
 * A counted noun. `plural('ticket.units', 1)` reads `ticket.units.one`, and 3 reads
 * `ticket.units.other` — `{count}` is filled in for you.
 *
 * The bases are derived from the keys that end in `.other`, so a base only exists once both
 * forms do. English and German need exactly these two; a locale with more would add its own
 * forms to the bundle and `Intl.PluralRules` would pick them.
 */
type BaseOf<K> = K extends `${infer Base}.other` ? Base : never
export type PluralBase = BaseOf<Key>

export function plural(base: PluralBase, count: number, params?: Params): string {
  const form = formats().plural.select(count)
  const key = `${base}.${form}` as Key
  const known = Object.prototype.hasOwnProperty.call(en, key)
  return t(known ? key : (`${base}.other` as Key), { count, ...params })
}
